import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { APP_CONFIG } from '../../config/appConfig';
import tracker from '../../utils/tracker';

const BASE_URL = APP_CONFIG.API_BASE_URL;

// Shared behavior of the sidebar "Download App" and Wallets "Start Finding"
// buttons: without an active plan (user data comes from verify-token via
// AuthContext) the user is sent to the purchase page, otherwise a fresh
// download link is generated and opened.
export const useDownloadApp = () => {
  const navigate = useNavigate();
  const { user, refreshAuth } = useContext(AuthContext);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const handleDownload = async () => {
    tracker.track('cta_click', { cta: 'download_app' });
    const hasActivePlan = Boolean(user?.planId) && user?.status === 'active';
    if (!hasActivePlan) {
      navigate('/purchase');
      return;
    }

    setGeneratingLink(true);
    setDownloadError('');

    try {
      const response = await fetch(`${BASE_URL}/download/generate-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error === 'No active plan found') {
          navigate('/purchase');
        } else if (data.expired) {
          setDownloadError('Your plan has expired. Please renew to continue.');
        } else {
          setDownloadError(data.error || 'Failed to generate download link');
        }
        return;
      }

      if (data.downloadUrl) {
        await refreshAuth();
        window.open(data.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Error generating download link:', error);
      setDownloadError('Failed to generate download link. Please try again.');
    } finally {
      setGeneratingLink(false);
    }
  };

  return { handleDownload, generatingLink, downloadError };
};
